/**
 * JuiceSheets overlay — projections, bye weeks, and each platform's own
 * positional ranks (ESPN / Sleeper / Yahoo) merged onto the KeepTradeCut board.
 *
 * KTC values are a crowdsourced trade-market number; they say nothing about how
 * many points a player is expected to score, and nothing about bye weeks. This
 * fills both gaps, and the spread between the three platforms' ranks shows where
 * the market disagrees.
 *
 * The sheet only covers QB/RB/WR/TE, so this is strictly additive: rows without
 * a match keep every field they already had.
 */

import { canonicalTeam, normalizeNameKey } from './playerMatch.js';

const JUICESHEETS_JSON = '/juicesheets-combined.json';

/** Which projection column matches a league's PPR setting. */
export const scoringKeyForPpr = (pointsPerReception) => {
  const ppr = Number(pointsPerReception) || 0;
  if (ppr >= 1) return 'ppr';
  if (ppr > 0) return 'half';
  return 'std';
};

export const SCORING_LABELS = { std: 'Standard', half: 'Half PPR', ppr: 'Full PPR' };

/** Platform rank fields that apply to a given scoring setting. */
const platformFieldsFor = (scoringKey) => {
  if (scoringKey === 'ppr') {
    return { ESPN: 'espnPpr', Sleeper: 'sleeperPpr', Yahoo: 'yahoo' };
  }
  if (scoringKey === 'half') {
    // Neither ESPN nor Yahoo publish a separate half-PPR list; their PPR board
    // is the closer of the two to a half-PPR league.
    return { ESPN: 'espnPpr', Sleeper: 'sleeperHalf', Yahoo: 'yahoo' };
  }
  return { ESPN: 'espnStd', Sleeper: 'sleeperStd', Yahoo: 'yahoo' };
};

export const fetchJuiceSheets = async () => {
  const response = await fetch(JUICESHEETS_JSON);
  if (!response.ok) {
    throw new Error(`Could not load JuiceSheets data (${response.status}).`);
  }
  const payload = await response.json();
  if (!Array.isArray(payload?.players) || !payload.players.length) {
    throw new Error('JuiceSheets data was empty.');
  }
  return payload;
};

let juiceSheetsCache = null;

export const getJuiceSheetsCached = async () => {
  if (juiceSheetsCache) return juiceSheetsCache;
  juiceSheetsCache = await fetchJuiceSheets();
  return juiceSheetsCache;
};

const matchKey = (name, position, team) =>
  `${normalizeNameKey(name)}|${position}|${canonicalTeam(team)}`;

/**
 * Average of the platform positional ranks that are present. Returns null when
 * no platform ranks the player, so "unranked everywhere" stays distinguishable
 * from "ranked 1st everywhere".
 */
export const consensusPositionalRank = (platform, scoringKey) => {
  const fields = platformFieldsFor(scoringKey);
  const ranks = Object.values(fields)
    .map((field) => platform?.[field])
    .filter((r) => typeof r === 'number' && r > 0);

  if (!ranks.length) return null;
  return ranks.reduce((sum, r) => sum + r, 0) / ranks.length;
};

/** Per-platform ranks in display order, for the board's cross-platform cell. */
export const platformRankList = (platform, scoringKey) =>
  Object.entries(platformFieldsFor(scoringKey)).map(([label, field]) => ({
    label,
    rank: typeof platform?.[field] === 'number' && platform[field] > 0 ? platform[field] : null,
  }));

/**
 * Merge JuiceSheets rows onto board rows by name + position + team.
 *
 * `consensusGap` is KTC's positional rank minus the platforms' average: positive
 * means the platforms rank the player better than KTC does.
 */
export const mergeJuiceSheets = (board, players, scoringKey = 'half') => {
  const byKey = new Map();
  const byNamePos = new Map();

  for (const p of players) {
    const position = p.position === 'PK' ? 'K' : p.position === 'DEF' ? 'DST' : p.position;
    byKey.set(matchKey(p.name, position, p.team), p);
    // Fallback bucket for players whose listed team differs between sources.
    const np = `${normalizeNameKey(p.name)}|${position}`;
    if (!byNamePos.has(np)) byNamePos.set(np, []);
    byNamePos.get(np).push(p);
  }

  let matched = 0;

  const merged = board.map((row) => {
    let hit = byKey.get(matchKey(row.name, row.position, row.team));
    if (!hit) {
      const candidates = byNamePos.get(`${normalizeNameKey(row.name)}|${row.position}`) || [];
      if (candidates.length === 1) hit = candidates[0];
    }
    if (!hit) return row;

    matched += 1;
    const proj = hit.proj?.[scoringKey] ?? null;
    const consensus = consensusPositionalRank(hit.platform, scoringKey);
    const gap = consensus != null && row.posRank != null ? row.posRank - consensus : null;

    return {
      ...row,
      proj,
      bye: hit.bye ?? null,
      juiceRank: hit.posRank?.[scoringKey] || null,
      platformRanks: platformRankList(hit.platform, scoringKey),
      siteRanks: Object.fromEntries(
        platformRankList(hit.platform, scoringKey).map((pr) => [pr.label, pr.rank]),
      ),
      consensusPosRank: consensus,
      consensusGap: gap,
    };
  });

  return { board: merged, matched, total: players.length };
};

/**
 * How far the platforms disagree with KTC about a player, as a label the board
 * can show. Only fires on gaps big enough to matter at the position.
 */
export const consensusSignal = (row) => {
  if (row?.consensusGap == null) return null;
  const gap = row.consensusGap;
  // A three-spot gap at QB means more than at WR, where the pool is far deeper.
  const threshold = row.position === 'WR' || row.position === 'RB' ? 6 : 3;

  if (gap >= threshold) return { level: 'higher', text: 'Sites like him more', gap };
  if (gap <= -threshold) return { level: 'lower', text: 'Sites like him less', gap };
  return null;
};
